import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import { DEFAULT_ITEM_ID_COLUMN_NAME } from '@/tableDefinitionEditor/columnRules/columnRuleBuilder.constants';
import { sortNewDerivedFeaturesByDependencies } from '@/tools/models/templateFormatting.utilities';
import { OutputTypeEnum } from '@seeqdev/api/model/InputFeatureV1';
import { TypeEnum } from '@seeqdev/api/model/TemplateRuleInputV1String';
import type { ColumnDefinition } from '@/tableDefinitionEditor/tableDefinition.types';
import type { NewlyAddedDerivedFeature, NewlyAddedInputFeature } from '@/tools/models/template.types';
import type { ColumnRuleOutputV1 } from '@seeqdev/api/model/ColumnRuleOutputV1';
import type { FeatureRuleV1 } from '@seeqdev/api/model/FeatureRuleV1';
import type { TemplateRuleInputV1Object } from '@seeqdev/api/model/TemplateRuleInputV1Object';

/**
 * Resolves a column ID from a formulaCreator rule's inputs array to the correct
 * TemplateRuleInputV1Object reference. Input columns become inputFeatureId references;
 * intermediates and outputs become derivedFeatureIndex references.
 *
 * @internal Exported for testing
 */
export const resolveRuleInput = (
  columnId: string,
  paramName: string,
  newInputFeatures: NewlyAddedInputFeature[],
  newDerivedFeatures: NewlyAddedDerivedFeature[],
): TemplateRuleInputV1Object => {
  const inputFeatureIndex = newInputFeatures.findIndex((f) => f.referenceId === columnId);
  if (inputFeatureIndex !== -1) {
    const inputFeature = newInputFeatures[inputFeatureIndex];
    return {
      name: inputFeature.name,
      description: inputFeature.description ?? '',
      type: TypeEnum.FEATUREID,
      inputFeatureIndex,
    };
  }

  const derivedIndex = newDerivedFeatures.findIndex((f) => (f.referenceId ?? f.id) === columnId);
  if (derivedIndex !== -1) {
    const derivedFeature = newDerivedFeatures[derivedIndex];
    return {
      name: derivedFeature.name,
      description: '',
      type: TypeEnum.FEATUREID,
      derivedFeatureIndex: derivedIndex,
    };
  }

  return {
    name: paramName,
    type: TypeEnum.ITEMID,
    argument: columnId,
  };
};

/**
 * Splits a pipe-delimited string into an array of trimmed, non-empty strings.
 * Returns an empty array if the input is not a string.
 */
const splitPipeDelimited = (value: unknown): string[] =>
  typeof value === 'string'
    ? value
        .split('|')
        .map((p) => p.trim())
        .filter((p) => p.length > 0)
    : [];

/**
 * Converts a column-side formulaCreator ColumnRuleOutputV1 into the FeatureRuleV1 shape
 * expected by the template API. Only formulaCreator rules are supported for scaling table
 * template outputs.
 *
 * Pre-populating formulaCreator here allows fetchFormulasForFeatures to short-circuit
 * the formula fetch for these features, since it skips features that already have
 * rules[0].formulaCreator set. Returns undefined if required arguments are missing,
 * allowing downstream logic to fall back to fetching formulas.
 *
 * @internal Exported for testing
 */
export const columnRuleToFeatureRule = (
  columnRule: ColumnRuleOutputV1,
  newInputFeatures: NewlyAddedInputFeature[],
  newDerivedFeatures: NewlyAddedDerivedFeature[],
): FeatureRuleV1 | undefined => {
  const { arguments: args, inputs = [] } = columnRule;

  if (!args?.formula || !args?.name) {
    return undefined;
  }

  const paramNames = args.parameters && args.parameters.trim() ? args.parameters.split('|').map((p) => p.trim()) : [];
  const variableParameterNames = splitPipeDelimited(args.variableParameters);
  const variableParameterStringValues = splitPipeDelimited(args.variableParameterStrings);
  const ruleInputs: TemplateRuleInputV1Object[] = inputs.map((columnId, i) =>
    resolveRuleInput(columnId, paramNames[i] ?? columnId, newInputFeatures, newDerivedFeatures),
  );

  return {
    inputs: ruleInputs,
    formulaCreator: {
      formula: { argument: args.formula, name: 'formula', type: TypeEnum.TEXT },
      name: { argument: args.name, name: 'name', type: TypeEnum.TEXT },
      parameters: { argument: paramNames, name: 'parameters', type: TypeEnum.TEXT },
      variableParameters: { argument: variableParameterNames, name: 'variableParameters', type: TypeEnum.TEXT },
      variableParameterStrings: {
        argument: variableParameterStringValues,
        name: 'variableParameterStrings',
        type: TypeEnum.TEXT,
      },
      ...(args.scopedTo && {
        scopedTo: { argument: args.scopedTo, name: 'scopedTo', type: TypeEnum.ITEMID },
      }),
    },
  };
};

export const toTemplateInputsFromTable = (
  ids: string[],
  inputs: NewlyAddedInputFeature[],
  columnIdMap: Map<string, ColumnDefinition>,
): NewlyAddedInputFeature[] => {
  const existingById = new Map((inputs ?? []).map((i) => [i.referenceId, i]));

  return ids
    .map((id) => {
      const existing = existingById.get(id);
      if (existing) {
        return existing;
      }

      const col = columnIdMap.get(id);
      if (!col) {
        return null;
      }

      return {
        referenceId: col.id,
        id: col.id,
        name: col.displayName,
        isCollapsed: false,
        description: col.description || '',
        originalName: col.displayName,
      };
    })
    .filter((input): input is NewlyAddedInputFeature => !!input);
};

export const toTemplateIntermediatesFromTable = (
  ids: string[],
  columnIdMap: Map<string, ColumnDefinition>,
  newInputFeatures: NewlyAddedInputFeature[],
  newDerivedFeatures: NewlyAddedDerivedFeature[],
): NewlyAddedDerivedFeature[] =>
  ids
    .map((id) => columnIdMap.get(id))
    .filter((c): c is ColumnDefinition => !!c)
    .map((column) => {
      const rule = column.rules?.[0];
      const featureRule = rule ? columnRuleToFeatureRule(rule, newInputFeatures, newDerivedFeatures) : undefined;
      return {
        referenceId: column.id,
        id: column.id,
        name: column.displayName,
        isOutput: false,
        outputType: OutputTypeEnum.ITEMID,
        outputItemType: column.outputItemType,
        outputItemMetadata: column.outputItemMetadata,
        rules: featureRule ? [featureRule] : [],
      };
    });

/** Validates that a column can be used as a template output. Only columns with rules of type 'formulaCreator' are
 *  supported in the backend, for now. */
export const isValidOutputColumn = (column: ColumnDefinition): boolean => {
  const validRules = [SeeqNames.MaterializedTables.Rules.FormulaCreator];
  const rules = column.rules ?? [];

  if (rules.length === 0) return false;
  return rules.every((rule) =>
    validRules.includes(rule.rule as typeof SeeqNames.MaterializedTables.Rules.FormulaCreator),
  );
};

/**
 * Recursively finds all input and intermediate column IDs for a given starting column ID.
 * Only includes DEFAULT_ITEM_ID_COLUMN_NAME if it is a direct input to an output.
 */
export const getInputAndIntermediateIds = (
  startId: string,
  visitedIds: string[],
  inputs: string[],
  intermediates: string[],
  outputIds: string[],
  columnIdMap: Map<string, ColumnDefinition>,
  isDirectInputToOutput = false,
) => {
  if (visitedIds.includes(startId)) {
    return;
  }
  visitedIds.push(startId);

  const col = columnIdMap.get(startId);
  if (!col) {
    return;
  }

  // If this column is already classified as an input or intermediate from a previous output traversal,
  // don't recurse into its dependencies again. This prevents incorrectly exploring dependencies of
  // non-creator rule columns that have already been identified as inputs.
  if (inputs.includes(startId) || intermediates.includes(startId)) {
    return;
  }

  const directInputs: string[] = [];
  const isSearchItemsDisplayName = (name: string) => name === DEFAULT_ITEM_ID_COLUMN_NAME;
  col.rules?.forEach((r) => r.inputs?.forEach((inp) => directInputs.push(inp)));

  if (directInputs.length === 0) {
    // Only include DEFAULT_ITEM_ID_COLUMN_NAME if it's a direct input to an output
    if (
      !inputs.includes(startId) &&
      !outputIds.includes(startId) &&
      (!isSearchItemsDisplayName(col.displayName) || isDirectInputToOutput)
    ) {
      inputs.push(startId);
    }
    return;
  }

  if (
    !isValidOutputColumn(col) &&
    !outputIds.includes(startId) &&
    !inputs.includes(startId) &&
    (!isSearchItemsDisplayName(col.displayName) || isDirectInputToOutput)
  ) {
    // Non-creator rule (e.g., findDescendant, itemSearch, getItemProperty) - add as input and stop recursing.
    // Model inputs should only come from columns that are not creator rules, and we should not walk into
    // their dependencies since those are implementation details of how the finder rule works.
    inputs.push(startId);
    return;
  } else if (!outputIds.includes(startId) && !intermediates.includes(startId) && isValidOutputColumn(col)) {
    intermediates.push(startId);
  }

  // Only recurse into dependencies for columns with formula creator rules
  // For each direct input, pass true if the current column is an output
  directInputs.forEach((depId) =>
    getInputAndIntermediateIds(
      depId,
      visitedIds,
      inputs,
      intermediates,
      outputIds,
      columnIdMap,
      outputIds.includes(startId),
    ),
  );
};

/**
 * Given a set of output columns, recursively finds all input and intermediate columns,
 * then builds NewlyAddedInputFeature[] and NewlyAddedDerivedFeature[] for use with
 * createAndPublishTemplate. Uses a two-pass approach — stubs are built first to establish
 * the full derived feature index order, then rules are applied in a second pass so that
 * cross-references between outputs and intermediates resolve to correct derivedFeatureIndex
 * values.
 *
 * Pure computation — no store writes. See getTemplateInputsAndIntermediatesFromTable in
 * templateColumn.actions.ts for the store-writing wrapper.
 */
export const computeTemplateInputsAndIntermediatesFromTable = (
  outputColumns: ColumnDefinition[],
  currentInputs: NewlyAddedInputFeature[],
  allColumns: ColumnDefinition[],
): {
  newInputFeatures: NewlyAddedInputFeature[];
  newDerivedFeatures: NewlyAddedDerivedFeature[];
} => {
  const columnIdMap = new Map<string, ColumnDefinition>(allColumns.map((c) => [c.id, c]));
  const outputIds = outputColumns.map((c) => c.id);

  const inputIds: string[] = [];
  const intermediateIds: string[] = [];
  outputColumns.forEach((col) => {
    getInputAndIntermediateIds(col.id, [], inputIds, intermediateIds, outputIds, columnIdMap, false);
  });

  const newInputFeatures = toTemplateInputsFromTable(inputIds, currentInputs, columnIdMap);

  // Build stubs without rules first so we have full index context for rule resolution
  const outputStubs: NewlyAddedDerivedFeature[] = outputColumns.map((column) => ({
    referenceId: column.id,
    id: column.id,
    name: column.displayName,
    isOutput: true,
    outputType: OutputTypeEnum.ITEMID,
    outputItemType: column.outputItemType,
    outputItemMetadata: column.outputItemMetadata,
    rules: [],
  }));

  const intermediateStubs: NewlyAddedDerivedFeature[] = intermediateIds
    .map((id) => columnIdMap.get(id))
    .filter((c): c is ColumnDefinition => !!c)
    .map((column) => ({
      referenceId: column.id,
      id: column.id,
      name: column.displayName,
      isOutput: false,
      outputType: OutputTypeEnum.ITEMID,
      outputItemType: column.outputItemType,
      outputItemMetadata: column.outputItemMetadata,
      rules: [],
    }));

  // Full derived features array for index resolution — outputs first to match final array order
  const allDerivedStubs = [...outputStubs, ...intermediateStubs];

  // Second pass: apply rules with full context
  const newOutputFeatures = outputStubs.map((stub, i) => {
    const rule = outputColumns[i].rules?.[0];
    const featureRule = rule ? columnRuleToFeatureRule(rule, newInputFeatures, allDerivedStubs) : undefined;
    return {
      ...stub,
      rules: featureRule ? [featureRule] : [],
    };
  });

  const newIntermediateFeatures = intermediateStubs.map((stub) => {
    const column = columnIdMap.get(stub.referenceId!)!;
    const rule = column.rules?.[0];
    const featureRule = rule ? columnRuleToFeatureRule(rule, newInputFeatures, allDerivedStubs) : undefined;
    return {
      ...stub,
      rules: featureRule ? [featureRule] : [],
    };
  });

  return {
    newInputFeatures,
    newDerivedFeatures: sortNewDerivedFeaturesByDependencies([...newOutputFeatures, ...newIntermediateFeatures]),
  };
};
